import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './FileArea.module.scss';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import BtButton from '../bt-button/bt-button';
import { formatDateTime, formatDistanceToNow, humanizeBytes } from '../../util';
import DownloadButton from '../../components/shared/DownloadButton';
import { ReactComponent as FileIconBackground } from '../../assets/svg/file-icon-background-darker.svg';
import { ReactComponent as ArrowIcon } from '../../assets/svg/arrow_stroke_down.svg';
import { ReactComponent as SuccessIcon } from '../../assets/svg/successIcon.svg';
import { ReactComponent as FolderIcon } from '../../assets/svg/folderIcon.svg';
import { ReactComponent as UpArrowIcon } from '../../assets/svg/upArrowIcon.svg';
import { ReactComponent as NavigationArrowIcon } from '../../assets/svg/navigationArrowIcon.svg';
import { ReactComponent as UploadIcon } from '../../assets/svg/uploadIcon.svg';
import { ReactComponent as CreateFolder } from '../../assets/svg/createFolderIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/svg/deleteIcon.svg';
import { ReactComponent as RenameIcon } from '../../assets/svg/renameIcon.svg';
import { CustomSort } from '../../components/shared/CustomSort';
import { Sort } from '../../services/service-supplier-monitoring';
import serviceFiles, { FileNode, FileOrFolder } from '../../services/service-files';
import servicePurchaseFiles from '../../services/service-purchase-files';
interface FileService {
  uploadFile(root: string, path: string, file: File, onUploadProgress: (progressEvent: AxiosProgressEvent) => void, options?: { ttl?: boolean, quotationId?: string }): Promise<FileNode>;
  listFiles(root: string, path: string, options?: { quotationId?: string }): Promise<{ children: FileOrFolder[] }>;
  createFolder(root: string, path: string, name: string, options?: { ttl?: boolean, quotationId?: string }): Promise<FileNode>;
  deleteFileOrFolder(root: string, path: string, options?: { quotationId?: string }): Promise<void>;
  renameFileOrFolder(root: string, path: string, oldName: string, newName: string, options?: { quotationId?: string }): Promise<FileNode>;
}
import Spinner from '../../components/shared/Spinner/Spinner';
import { ErrorMessageIcon } from '../../components/shared/ErrorMessageIcon/ErrorMessageIcon';
import appState from '../../state/AppStateContainer';
import axios, { AxiosProgressEvent } from 'axios';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import { TinyRippler } from '../../components/shared/TinyRippler/TinyRippler';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { NotificationManager } from 'react-notifications';
import * as Sentry from '@sentry/react';
import { Trans, useTranslation } from 'react-i18next';
import { isPreviewable } from '../../components/shared/FilePreviewViewer/FilePreviewViewer';
import FilePreviewOverlay from '../../components/shared/FilePreviewOverlay/FilePreviewOverlay';

const genericCompare = (a: string | number | Date, b: string | number | Date, order: 'asc' | 'desc') => {
  if (typeof a === 'string' && typeof b === 'string') {
    return order === 'desc' ? b.toLowerCase().localeCompare(a.toLowerCase()) : a.toLowerCase().localeCompare(b.toLowerCase());
  }
  if (a < b) {
    return order === 'asc' ? 1 : -1;
  } else if (a > b) {
    return order === 'asc' ? -1 : 1
  } else {
    return 0;
  }
}

const FILE_SERVICE_CONFIG: Record<string, FileService> = {
  '/purchase-files': servicePurchaseFiles,
  '/bid-request-files': serviceFiles,
  '/bid-files': serviceFiles,
  '/files': serviceFiles,
};

const getFileService = (root: string): FileService => {
  const match = Object.keys(FILE_SERVICE_CONFIG).find(prefix => root.startsWith(prefix));
  return match ? FILE_SERVICE_CONFIG[match] : serviceFiles;
};

const FileIcon = ({ fileName, className }: { fileName: string, className?: string }) => {
  const [icon, setIcon] = useState('');

  useEffect(() => {
    const importAll = (source: { keys: () => string[] } & ((filename: string) => string)) => {
      const images: { [filename: string]: string } = {};
      source.keys().forEach(item => { images[item.replace('./', '')] = source(item); });
      return images
    }

    const getFileExtension = (fileName: string) => {
      return fileName?.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2);
    }

    const checkImage = (src: string, bad: () => void) => {
      const img = new Image();
      img.onerror = bad;
      img.src = src;
    }

    const images = importAll(require.context('../../assets/file-icons/', false, /\.(png|jpe?g|svg)$/));
    const tempFileIcon = images[`${getFileExtension(fileName.toLowerCase())}.svg`];
    const tempIcon = tempFileIcon ? tempFileIcon : images['blank.svg'];
    setIcon(tempIcon);
    checkImage(tempIcon, () => {
      setIcon(images['blank.svg']);
    });
  }, [])

  return (
    <div className={`${styles.fileIconContainer} ${className}`}>
      <FileIconBackground />
      <img src={icon} />
    </div>
  )
}

type FileRowProps = {
  root: string,
  path: string,
  file: ManagedFile,
  highlightedFile: string | undefined,
  setHighlightedFile: React.Dispatch<React.SetStateAction<string | undefined>>,
  selectedFiles: string[],
  setSelectedFiles: React.Dispatch<React.SetStateAction<string[]>>,
  disabled?: boolean,
  setPath: (getNewPath: (path: string) => string) => void,
  files: ManagedFile[],
  onDoubleClick: () => void,
  onPreview: () => void,
  onDownload: () => void,
}

type Upload = {
  uploadId: string,
  name: string,
  size: number,
  progress: number,
  errorMessage?: string,
  path: string,
};

const FileRow: FC<FileRowProps> = ({ root, path, file, highlightedFile, setHighlightedFile, selectedFiles, setSelectedFiles, disabled, setPath, files, onDoubleClick, onPreview, onDownload }) => {
  const [t] = useTranslation('files');
  const createdAt = new Date(file.createdAt);
  const disabledRow = disabled || file.uploadId || file.errorMessage;
  const orgSlug = appState.getCurrentOrgSlug();
  const os = (navigator.platform.match(/mac|win|linux/i) || ['other'])[0].toLowerCase();
  const modifierKey = os == 'mac' ? 'metaKey' : 'ctrlKey';
  const indexOfHighlightedFile = files.findIndex(f => f._id === highlightedFile);
  const isDownloading = !!(file.progress && file.progress < 1);

  return (
    <tr
      className={`
        ${highlightedFile === file._id ? styles.highlighted : ''}
        ${selectedFiles.includes(file._id) ? styles.selected : ''}
        ${disabledRow ? styles.disabledRow : ''}
      `}
      onDoubleClick={(e) => {
        if (disabledRow || (e.target as HTMLInputElement)?.type === 'checkbox') return;
        onDoubleClick();
        setHighlightedFile(file._id);
      }}
      onClick={(e) => {
        document.getSelection()?.empty();
        if (disabledRow) return;

        if (e.shiftKey && indexOfHighlightedFile !== -1) {
          const indexOfTarget = files.findIndex(f => f._id === file._id);

          let fromIndex;
          let toIndex;
          if (indexOfHighlightedFile > indexOfTarget) {
            fromIndex = indexOfTarget;
            toIndex = indexOfHighlightedFile + 1;
          } else {
            fromIndex = indexOfHighlightedFile;
            toIndex = indexOfTarget + 1;
          }

          const newFileIdsToBeSelected = files.slice(fromIndex, toIndex).map(f => f._id);
          setSelectedFiles(prev => {
            if (e[modifierKey]) {
              return Array.from(new Set([...prev, ...newFileIdsToBeSelected]));
            } else {
              return newFileIdsToBeSelected;
            }
          })

          return;
        }

        if (e[modifierKey]) {
          setSelectedFiles(prev => {
            if (prev.includes(file._id)) {
              return prev.filter(f => f !== file._id);
            } else {
              return [...prev, file._id];
            }
          });
          setHighlightedFile(file._id);
          return;
        }

        setSelectedFiles([file._id]);
        setHighlightedFile(file._id);
      }}
    >
      <td className={styles.fileNameColumn}>
        <DownloadButton
          url={`/api/organizations/${orgSlug}${root}${path}/${file.name}`}
          fileName={file.name}
          render={({onClick}) => {
            return (
              <div className={styles.file}>
                <div>
                  <StyledCheckbox
                    color="var(--light-blue-600)"
                    checked={selectedFiles.includes(file._id)}
                    onChange={() => {
                      if (disabledRow) return;
                      setSelectedFiles(prevState => {
                        if (prevState.includes(file._id)) {
                          return prevState.filter(f => f !== file._id);
                        } else {
                          return [...prevState, file._id];
                        }
                      });
                      setHighlightedFile(file._id);
                    }}
                    onClick={(e: MouseEvent) => e.stopPropagation()}
                    style={{ opacity: selectedFiles.length ? 1 : 0, visibility: disabledRow ? 'hidden' : 'visible', pointerEvents: disabledRow ? 'none' : 'auto' }}
                    className={styles.checkbox}
                  />

                  {file.type === 'FOLDER' ? (
                    <FolderIcon style={{ marginLeft: '2px', marginRight: '8px' }} />
                  ) : (
                    <FileIcon className={styles.fileIcon} fileName={file.name} />
                  )}
                  <span
                    onClick={(e) => {
                      if (!disabledRow) {
                        if (file.type === 'FOLDER') {
                          setPath(oldPath => {
                            const newPath = `${oldPath}${oldPath === '/' ? file.name : `/${file.name}`}`;
                            return newPath;
                          });
                        } else if (isPreviewable(file.name)) {
                          onPreview();
                        } else if (onClick) {
                          onClick();
                        }
                        e.stopPropagation();
                      }
                    }}
                    className={styles.fileName}
                  >
                    {file.name}
                  </span>
                </div>
              </div>
            )
          }}
        />
      </td>
      <td>{file.type == 'FILE' && humanizeBytes(file.size)}</td>
      <td title={formatDateTime(createdAt)}>{formatDistanceToNow(createdAt)}</td>
      <td>{file.createdBy}</td>
      <td className={styles.downloadColumn}>
        {file.type === 'FILE' && (
          file.errorMessage ? (
            <ErrorMessageIcon errorMessage={file.errorMessage} style={{ width: '19px' }} />
          ) : isDownloading ? (
            <Spinner size='xs' progress={file.progress ?? 0} />
          ) : file.doneAt ? (
            <SuccessIcon style={{ transform: 'scale(1.26)' }} />
          ) : !file.uploadId ? (
            <div
              className={styles.downloadButton}
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              title={t('button.download', 'Ladda ner')}
            >
              <DownloadIcon />
            </div>
          ) : null
        )}
      </td>
    </tr>
  )
}

type CustomSortHeaderProps = {
  sort: Sort,
  setSort: React.Dispatch<React.SetStateAction<Sort>>,
  field: string,
  children?: React.ReactNode
}

const CustomSortHeader: FC<CustomSortHeaderProps> = ({ sort, setSort, field, children }) => {
  return (
    <CustomSort sort={sort} setSort={setSort} field={field} className={styles.tableHeader}>
      {children}
      <ArrowIcon className={sort.field === field && sort.order ? styles[sort.order]: ''} width={10} />
    </CustomSort>
  )
}

type ManagedState = {
  errorMessage?: string,
  doneAt?: Date,
  progress?: number,
}

type ManagedFile = FileOrFolder  & ManagedState & {
  uploadId?: string,
}

type Props = {
  root: string,
  path: string,
  setPath: (getNewPath: (path: string) => string) => void,
  isAllowedToUpload?: boolean,
  isAllowedToDownload?: boolean,
  isAllowedToDelete?: boolean,
  hasTtl?: boolean,
  quotationId?: string,
  onUploadingChange?: (isUploading: boolean) => void,
  onFilesChanged?: () => void,
  orgId?: string,
}

let nextUploadId = 1;

const FileArea: FC<Props> = ({ root, path, setPath, isAllowedToUpload, isAllowedToDownload, isAllowedToDelete, hasTtl, quotationId, onUploadingChange, onFilesChanged, orgId }) => {
  const [files, setFiles] = useState<ManagedFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [highlightedFile, setHighlightedFile] = useState<string>();
  const [sort, setSort] = useState<Sort>({});
  const [uploads, setUploads] = useState<Map<string, Upload>>(new Map());
  useEffect(() => { onUploadingChange?.(uploads.size > 0); }, [uploads.size]);
  const [downloading, setDownloading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [deletingFiles, setDeletingFiles] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<ManagedFile | null>(null);
  const [t] = useTranslation('files');
  
  // Preview navigation

  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const orgSlug = appState.getCurrentOrgSlug();

  useEffect(() => {
    (async () => {
      setLoadingFiles(true);
      try {
        const fileService = getFileService(root);
        const files = await fileService.listFiles(root, path, { quotationId });
        setSelectedFiles([]);
        setHighlightedFile(undefined);
        setSort({});
        setFiles(files?.children || []);
      } catch (error) {
        console.error('Error loading files:', error);
        setFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    })();
  }, [path, root]);


  useEffect(() => {
    let entered = 0;
    let overed = 0;
    const dragEnter = (event : DragEvent) => {
      if (event.dataTransfer?.types.includes('Files')) {
        entered++;
        if (tbodyRef.current?.contains(event.target as Node)) {
          overed++;
        }
        setDragging(entered > 0);
        setDragOver(overed > 0);
      }
    }
    const dragLeave = (event : DragEvent) => {
      if (event.dataTransfer?.types.includes('Files')) {
        entered--;
        if (tbodyRef.current?.contains(event.target as Node)) {
          overed--;
        }
        setDragging(entered > 0);
        setDragOver(overed > 0);
      }
    }
    const drop = (event : DragEvent) => {
      if (event.dataTransfer?.types.includes('Files')) {
        event.preventDefault();
        entered = 0;
        overed = 0;
        setDragging(false);
        setDragOver(false);
      }
    }
    const dragOver = (event : DragEvent) => {
      if (event.dataTransfer?.types.includes('Files')) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = tbodyRef.current?.contains(event.target as Node) ? 'copy' : 'none';
        }
      }
    }

    window.addEventListener('dragenter', dragEnter);
    window.addEventListener('dragleave', dragLeave);
    window.addEventListener('drop', drop);
    window.addEventListener('dragover', dragOver);
    return () => {
      window.removeEventListener('dragenter', dragEnter);
      window.removeEventListener('dragleave', dragLeave);
      window.removeEventListener('drop', drop);
      window.removeEventListener('dragover', dragOver);
    }
  }, []);

  const sortedFiles = useMemo(() => {
    const filesAndUploads = [...files, ...(Array.from(uploads.values()).filter(u => u.path == path && !loadingFiles).map(u => ({
      id: u.uploadId,
      uploadId: u.uploadId,
      name: u.name,
      size: u.size,
      progress: u.progress,
      type: 'FILE',
      parentId: '',
      createdAt: new Date(),
      createdBy: appState.getUserName(),
      errorMessage: u.errorMessage,
    })))] as ManagedFile[];
    return [...filesAndUploads].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'FOLDER' ? -1 : 1;
      }
      const field = sort.field ?? 'name';
      const order = sort.order ?? 'asc';
      return genericCompare(a[field as keyof FileOrFolder]!, b[field as keyof FileOrFolder]!, order);
    });
  }, [files, sort, uploads, loadingFiles]);

  // Get only previewable files for navigation
  const previewableFiles = useMemo(() => {
    return sortedFiles.filter(f => f.type === 'FILE' && isPreviewable(f.name));
  }, [sortedFiles]);

  const currentPreviewIndex = useMemo(() => {
    if (!previewFile) return -1;
    return previewableFiles.findIndex(f => f._id === previewFile._id);
  }, [previewableFiles, previewFile]);

  const goToPreviousFile = useCallback(() => {
    if (currentPreviewIndex > 0) {
      setPreviewFile(previewableFiles[currentPreviewIndex - 1]);
    }
  }, [currentPreviewIndex, previewableFiles]);

  const goToNextFile = useCallback(() => {
    if (currentPreviewIndex < previewableFiles.length - 1) {
      setPreviewFile(previewableFiles[currentPreviewIndex + 1]);
    }
  }, [currentPreviewIndex, previewableFiles]);

  const removeFiles = useCallback(async (fileIds: string[]) => {
    if (!isAllowedToDelete) return;
    setDeletingFiles(true);
    setShowDeleteModal(false);
    setSelectedFiles([]);
    await Promise.all(fileIds.map(async fileId => {
      const file = files.find(file => file._id === fileId);
      if (file) {
        const fileService = getFileService(root);
        await fileService.deleteFileOrFolder(root, `${path}/${file.name}`, { quotationId });
      }
    }));
    setFiles(files.filter(file => !fileIds.includes(file._id)));
    onFilesChanged?.();
    setDeletingFiles(false);
  }, [root, path, files, isAllowedToDelete]);

  const downloadFiles = useCallback(async (filesIds: string[]) => {
    if (!isAllowedToDownload) return;
    setDownloading(true);
    const filesToDownload: (FileNode & ManagedState)[] = filesIds.map(fileId => files.find(file => file._id === fileId)) as (FileNode & ManagedState)[];
    await Promise.all(filesToDownload.filter(file => file.type === 'FILE').map(async file => {
      if (file) {
        try {
          const response = await axios.get(`/api/organizations/${orgSlug}${root}${path}/${file.name}`, {
            params: quotationId ? { quotationId } : undefined,
            onDownloadProgress: (e: AxiosProgressEvent) => {
              setFiles(prev => {
                const prevFile = prev.find(f => f._id === file._id);
                if (prevFile) {
                  prevFile.progress = e.loaded / (e?.total || 1);
                }
                return [...prev];
              });
            },
            responseType: 'blob',
          });

          fileDownload(
            response.data,
            file.name,
            file.mimeType,
          );

          setSelectedFiles(prevState => prevState.filter(f => f !== file._id));
        } catch (error) {
          const errorMsg = _.get(error, 'response.data.error', t('errorMessage.unexpected', 'Oväntat fel ({{error}})', { error }));
          setFiles(prev => {
            const prevFile = prev.find(f => f._id === file._id);
            if (prevFile) {
              prevFile.errorMessage = errorMsg;
            }
            return prev;
          })
        }
      }
    }));

    setDownloading(false);
  }, [files, root, path, isAllowedToDownload]);

  const downloadFilesAsZip = useCallback(async (fileIds: string[], orgId: string) => {
    if (!isAllowedToDownload) return;
    setZipping(true);
    try {
      const blob = await serviceFiles.downloadZip(fileIds, orgId);
      fileDownload(blob, 'files.zip', 'application/zip');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error ?? (error as Error).message ?? t('errorMessage.unexpected', 'Oväntat fel ({{error}})', { error });
      NotificationManager.error(errorMsg);
    } finally {
      setZipping(false);
    }
  }, [isAllowedToDownload]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!isAllowedToUpload) return;
    const promises = [];
    for (let i = 0; i < files.length; i++) {
      const uploadId = '$upload-id-' + (nextUploadId++);
      const file = files[i];
      const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        setUploads(prev => {
          const map = new Map(prev);
          const upload = map.get(uploadId) ?? { uploadId, name: file.name, size: file.size, progress: 0, path };
          return map.set(uploadId, { ...upload, progress: progressEvent.progress ?? 0 });
        });
      }
      setUploads(prev => {
        const map = new Map(prev);
        const existingUploadsWithError = Array.from(map.values()).filter(u => u.name == file.name && u.errorMessage);
        for (const u of existingUploadsWithError) {
          map.delete(u.uploadId);
        }
        const upload = { uploadId, name: file.name, size: file.size, progress: 0, path };
        return map.set(uploadId, upload);
      });
      const fileService = getFileService(root);
      promises.push(fileService.uploadFile(root, path, file, onUploadProgress, { ttl: hasTtl, quotationId }).then((uploadedFile) => {
        setUploads(prev => {
          const map = new Map(prev);
          const upload = map.get(uploadId) ?? { uploadId, name: file.name, size: file.size, progress: 0, path };
          if (upload.path == path) {
            setFiles(prev => [...prev, { ...uploadedFile, doneAt: new Date() }]);
            onFilesChanged?.();
          }
          map.delete(uploadId);
          return map;
        });
        setTimeout(() => {
          setFiles(prev => prev.map(f => {
            if (f._id === uploadedFile._id) {
              f.doneAt = undefined;
            }
            return f;
          }));
        }, 5000);
      }).catch((error) => {
        setUploads(prev => {
          const map = new Map(prev);
          const upload = map.get(uploadId) ?? { uploadId, name: file.name, size: file.size, progress: 0, path };
          return map.set(uploadId, { ...upload, errorMessage: error.response?.data?.error ?? error.message });
        });
      }));
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    await Promise.all(promises);
  }, [root, path, isAllowedToUpload, hasTtl]);

  const createFolder = useCallback(async (name: string) => {
    if (!isAllowedToUpload) return;
    if (!name || name.trim() === '' || name.includes('/')) return;

    setCreatingFolder(true);
    try {
      const fileService = getFileService(root);
      const folder = await fileService.createFolder(root, path, name, { ttl: hasTtl, quotationId });
      setFiles(prev => [...prev, { ...folder, createdAt: new Date(), createdBy: appState.getUserName(), }]);
      setShowCreateFolderModal(false);
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', t('errorMessage.unexpected', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.unsuccessful.title', 'Misslyckat'));
      Sentry.captureException(error);
    } finally {
      setCreatingFolder(false);
    }
  }, [root, path, isAllowedToUpload, hasTtl]);

  const renameFileOrFolder = useCallback(async (name: string) => {
    if (!isAllowedToUpload) return;
    setShowRenameModal(false);
    const oldName = files.find(f => f._id === selectedFiles[0])?.name;
    if (!oldName) throw new Error('Renaming unknown file');
    try {
      const fileService = getFileService(root);
      const response = await fileService.renameFileOrFolder(root, path, oldName, name, { quotationId });
      setFiles(prev => prev.map(f => f.name === oldName ? response : f));
    } catch (error : any) {
      const message = error?.response?.data?.error ?? (error as Error).message ?? '';
      setFiles(prev => prev.map(f => f.name === oldName ? { ...f, errorMessage: message } : f));
    }
  }, [root, path, files, selectedFiles, isAllowedToUpload]);

  const popPath = useCallback(() => {
    setPath(prev => {
      let newPath = prev.split('/').slice(0, -1).join('/');
      if (newPath === '') newPath = '/';
      return newPath;
    })
  }, [path]);

  const isDownloadButtonDisabled = !!selectedFiles.find(fileId => files.find(f => f._id === fileId && f.type === 'FOLDER'));
  const selectedFileIds = selectedFiles.filter(fileId => files.find(f => f._id === fileId && f.type === 'FILE'));
  const splitPath = path.split('/');

  const filesToDelete = selectedFiles.filter(fileId => files.find(f => f._id === fileId && f.type === 'FILE'));
  const foldersToDelete = selectedFiles.filter(fileId => files.find(f => f._id === fileId && f.type === 'FOLDER'));

  const fileNodesToDelete = useMemo(() => {
    const fileCount = filesToDelete.length;
    const folderCount = foldersToDelete.length;
    const context = fileCount ?
      folderCount ? 'bothFilesAndFolders' : 'onlyFiles'
      : folderCount ? 'onlyFolders' : '';
    return t(
      'modal.confirmDelete.intro.message',
      'Ta bort {{fileCount}} filer och {{folderCount}} mappar',
      { context, fileCount, folderCount }
    );
  }, [filesToDelete.length, foldersToDelete.length]);

  if (!isAllowedToUpload && !loadingFiles && sortedFiles.length === 0) return null;

  return (
    <div className={styles.container}>
      <div className={styles.buttons}>
        {!selectedFiles.length ? (<>
          {isAllowedToUpload && (
            <label style={{ letterSpacing: 'normal', marginBottom: '0px' }}>
              <BtButton color="white" size="xxs" disabled={downloading || deletingFiles} leftIcon={<UploadIcon />}>
                <input style={{ display: 'none' }} ref={inputRef} type="file" multiple onChange={event => event.target.files && uploadFiles(Array.from(event.target.files))} disabled={downloading || deletingFiles} />
                {t('button.uploadFiles', 'Ladda upp filer')}
              </BtButton>
            </label>
          )}
          {isAllowedToUpload && (
            <BtButton color="white" size="xxs" leftIcon={<CreateFolder />} disabled={downloading || deletingFiles} onClick={() => {
              setFolderName('');
              setShowCreateFolderModal(true);
            }}>
              {t('button.createFolder', 'Skapa mapp')}
            </BtButton>
          )}
          {isAllowedToDownload && files.length > 0 && (
            <BtButton
              color="white"
              size="xxs"
              onClick={() => downloadFilesAsZip(files.map(f => f._id), orgId as string)}
              loaderShow={zipping}
              leftIcon={<DownloadIcon />}
            >
              {t('button.downloadAllZip', 'Ladda ner alla som .zip')}
            </BtButton>
          )}
        </>) : (
          <>
            {isAllowedToDownload && (
              <TooltipContainer
                renderReferenceComponent={(className, ref) =>
                  <div ref={ref} className={className}>
                    <BtButton
                      color="white"
                      size="xxs"
                      onClick={() => downloadFiles(selectedFiles)}
                      loaderShow={downloading}
                      disabled={isDownloadButtonDisabled}
                      leftIcon={<DownloadIcon />}
                    >
                      {t('button.download', 'Ladda ner')}
                    </BtButton>
                  </div>
                }
              >
                {isDownloadButtonDisabled && <span style={{ color: 'var(--gray-700)', fontSize: '12px' }}>{t('tooltip.cannotDownloadFolders', 'Det går inte att ladda ner mappar.')}</span>}
              </TooltipContainer>
            )}
            {isAllowedToDownload && selectedFiles.length > 1 && (
              <BtButton
                color="white"
                size="xxs"
                onClick={() => downloadFilesAsZip(selectedFiles, orgId as string)}
                loaderShow={zipping}
                leftIcon={<DownloadIcon />}
              >
                {t('button.downloadZip', 'Ladda ner som .zip')}
              </BtButton>
            )}
            {isAllowedToDelete && !downloading && (
              <BtButton
                color="white"
                size="xxs"
                onClick={() => setShowDeleteModal(true)}
                loaderShow={deletingFiles}
                leftIcon={<DeleteIcon />}
              >
                {t('button.delete', 'Ta bort {{count}} fil', { count: selectedFiles.length })}
              </BtButton>
            )}
            {isAllowedToUpload && !downloading && selectedFiles.length === 1 && (
              <BtButton
                color="white"
                size="xxs"
                leftIcon={<RenameIcon />}
                onClick={() => {
                  setFolderName(files.find(f => f._id === selectedFiles[0])?.name ?? '');
                  setShowRenameModal(true);
                }}
              >
                {t('button.rename', 'Byt namn')}
              </BtButton>
            )}
          </>
        )}
      </div>
      <div className={`${styles.pathContainer} ${path === '/' ? styles.isRoot : ''}`}>
        <div onClick={() => !loadingFiles && popPath()}>
          <UpArrowIcon />
        </div>
        <div className={styles.path}>
          {path === '/' ? <span key={'static'}>{t('path.root', 'Filer')}</span> : (
            splitPath.map((p, index) => {
              const pathKey = splitPath.slice(0, index + 1).join('/');
              return (
                <>
                  {index > 0 && <NavigationArrowIcon key={pathKey + 'arrow'} className={styles.arrow} />}
                  <span
                    key={pathKey}
                    onClick={() => {
                      if (splitPath.length - 1 !== index && !loadingFiles) {
                        setPath(() => pathKey);
                      }
                    }}
                  >
                    {p || t('path.root', 'Filer')}
                  </span>
                </>
              )
            })
          )}
          <TinyRippler show={loadingFiles || deletingFiles} />
        </div>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>
              <CustomSortHeader
                sort={sort}
                setSort={setSort}
                field="name"
              >
                <StyledCheckbox
                  dash={selectedFiles.length ? selectedFiles.length !== files.length : false}
                  color="var(--light-blue-600)"
                  checked={!!selectedFiles.length}
                  onChange={() => {
                    if (downloading) return;
                    setSelectedFiles((prevState) => prevState.length ? [] : files.filter(f => !f.uploadId).map(f => f._id));
                    setHighlightedFile(undefined);
                  }}
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation();
                  }}
                  style={{ visibility: downloading ? 'hidden' : 'visible' }}
                />
                {t('table.header.fileName', 'Namn')}
              </CustomSortHeader>
            </th>
            <th>
              <CustomSortHeader
                sort={sort}
                setSort={setSort}
                field='size'
              >
                {t('table.header.fileSize', 'Storlek')}
                </CustomSortHeader>
            </th>
            <th>
              <CustomSortHeader
                sort={sort}
                setSort={setSort}
                field='createdAt'
              >
                {t('table.header.createdAt', 'Datum')}
              </CustomSortHeader>
            </th>
            <th>
              <CustomSortHeader
                sort={sort}
                setSort={setSort}
                field='createdBy'
              >
                {t('table.header.createdBy', 'Användare')}
              </CustomSortHeader>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody ref={tbodyRef}
          onDrop={(e) => {
            e.preventDefault();
            const files : File[] = [];
            if (e.dataTransfer.items) {
              Array.from(e.dataTransfer.items).forEach((item) => {
                if (item.kind === 'file') {
                  const entry = item.webkitGetAsEntry();
                  if (entry?.isDirectory) {
                    NotificationManager.error(t('notification.error.cannotUploadFolders.description', 'Kan inte ladda upp hela mappar.'), t('notification.error.cannotUploadFolders.title', 'Fel'));
                    return;
                  }
                  const file = item.getAsFile();
                  if (file) {
                    files.push(file);
                  }
                }
              });
            } else {
              Array.from(e.dataTransfer.files).forEach((file) => {
                files.push(file);
              });
            }
            uploadFiles(files);
          }}
          className={`${isAllowedToUpload && dragging ? styles.dragging : ''} ${isAllowedToUpload && dragOver ? styles.dragOver : ''} `}
        >
          {sortedFiles.map(file => (
            <FileRow
              key={file._id}
              root={root}
              path={path}
              file={file}
              highlightedFile={highlightedFile}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              setHighlightedFile={setHighlightedFile}
              disabled={downloading || loadingFiles || deletingFiles || (file.type === 'FILE' && !isAllowedToDownload)}
              setPath={setPath}
              files={sortedFiles}
              onDoubleClick={() => {
                if (file.type === 'FILE') {
                  downloadFiles([file._id]);
                } else {
                  setPath(oldPath => {
                    const newPath = `${oldPath}${oldPath === '/' ? file.name : `/${file.name}`}`;
                    return newPath;
                  });
                }
              }}
              onPreview={() => {
                setPreviewFile(file);
                setShowPreviewModal(true);
              }}
              onDownload={() => downloadFiles([file._id])}
            />
          ))}
          {sortedFiles.length === 0 && !loadingFiles && isAllowedToUpload && (
            <tr className={styles.filler}>
              <td colSpan={5} className={styles.filler}>
                <span className={styles.dragAndDropText}>
                  <Trans t={t} i18nKey="table.body.placeholder">
                    Dra och släpp för att lägga till filer eller <a onClick={() => inputRef?.current?.click()}>bläddra</a>.
                  </Trans>
                </span>
              </td>
            </tr>
          )}
          {sortedFiles.length > 0 && sortedFiles.length < 3 && (
            <tr className={styles.filler}>
              <td colSpan={5} rowSpan={3 - sortedFiles.length}></td>
            </tr>
          )}
        </tbody>
      </table>

      <Modal
        title={t('modal.createFolder.title', 'Skapa mapp')}
        show={showCreateFolderModal}
        setShow={setShowCreateFolderModal}
        buttonInfo={{
          label: t('modal.createFolder.button.save', 'Skapa'),
          action: async () => {
            await createFolder(folderName);
          },
          loaderShow: creatingFolder,
          disabled: !folderName || folderName.trim() === '' || folderName.includes('/'),
        }}
      >
        <div>
          <Field
            label={t('modal.createFolder.label.name', 'Namn')}
            value={folderName}
            onChange={setFolderName}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await createFolder(folderName);
              }
            }}
          />
        </div>
      </Modal>
      <Modal
        title={t('modal.confirmDelete.title', 'Bekräfta borttagning')}
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        buttonInfo={{
          label: t('modal.confirmDelete.button.delete', 'Ta bort'),
          action: () => removeFiles(selectedFiles),
          loaderShow: deletingFiles,
          leftIcon: <DeleteIcon />,
          color: 'failure',
        }}
      >
        <div>
          <p style={{fontSize: '14px', color: 'var(--gray-700)'}}>{fileNodesToDelete}</p>
        </div>
      </Modal>
      <Modal
        title={t('modal.rename.title', 'Byt namn')}
        show={showRenameModal}
        setShow={setShowRenameModal}
        buttonInfo={{
          label: t('modal.rename.button.save', 'Spara'),
          action: async () => await renameFileOrFolder(folderName),
          disabled: !folderName || folderName.trim() === '' || folderName.includes('/'),
        }}
      >
        <div>
          <Field
            label={t('modal.rename.label.name', 'Nytt namn')}
            value={folderName}
            onChange={setFolderName}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await renameFileOrFolder(folderName);
              }
            }}
          />
        </div>
      </Modal>

      {showPreviewModal && previewFile && (
        <FilePreviewOverlay
          url={`/api/organizations/${orgSlug}${root}${path}/${previewFile.name}`}
          fileName={previewFile.name}
          onClose={() => setShowPreviewModal(false)}
          onPrevious={previewableFiles.length > 1 ? goToPreviousFile : undefined}
          onNext={previewableFiles.length > 1 ? goToNextFile : undefined}
          hasPrevious={currentPreviewIndex > 0}
          hasNext={currentPreviewIndex < previewableFiles.length - 1}
          counter={previewableFiles.length > 1 ? `${currentPreviewIndex + 1} / ${previewableFiles.length}` : undefined}
        />
      )}
    </div>
  )
}

export default FileArea;
